import { Check, Loader2, AlertTriangle, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { StatusChip, queueLabel } from "@/components/app/StatusChip";
import { cn } from "@/lib/utils";

const PHASES = [
  [5, "Reading the job description"],
  [18, "Extracting skills, experience and responsibilities"],
  [50, "Suggesting an ideal profile and interview focus"],
  [75, "Checking the JD for vague or exclusionary wording"],
  [92, "Saving the interpretation"],
];

export function AnalysisProgress({ qjob, onRetry, onBack }) {
  const progress = qjob?.progress ?? 0;
  const failed = qjob?.status === "failed";
  return (
    <div className="panel mx-auto max-w-2xl space-y-6 p-8" data-testid="analysis-progress">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">{failed ? "The analysis didn't finish" : "Analysing your job description"}</h2>
          <p className="text-sm text-ink-secondary">
            {failed ? qjob.error : "This usually takes 15–30 seconds. You can leave this page; we'll notify you when it's ready."}
          </p>
        </div>
        <StatusChip status={queueLabel(qjob?.status || "queued")} testId="analysis-status-chip" />
      </div>
      {!failed && (
        <>
          <Progress value={progress} className="h-2 bg-brand-tint" data-testid="analysis-progress-bar" />
          <p className="text-xs text-ink-muted" data-testid="analysis-progress-message">{qjob?.progress_message || "Queued"} · {progress}%</p>
          <ul className="space-y-2">
            {PHASES.map(([at, label], i) => {
              const nextAt = PHASES[i + 1]?.[0] ?? 101;
              const done = progress >= nextAt;
              const active = progress >= at && !done;
              return (
                <li key={label} className={cn("flex items-center gap-3 text-sm", done ? "text-ink" : active ? "text-ink" : "text-ink-muted")}>
                  <span className={cn("flex h-5 w-5 items-center justify-center rounded-full", done ? "bg-success text-white" : "border border-line")}>
                    {done ? <Check size={12} /> : active ? <Loader2 size={12} className="animate-spin text-brand" /> : null}
                  </span>
                  {label}
                </li>
              );
            })}
          </ul>
        </>
      )}
      {failed && (
        <div className="flex gap-3">
          <Button onClick={onRetry} data-testid="analysis-retry-btn"><RotateCcw size={16} /> Try again</Button>
          <Button variant="outline" onClick={onBack} data-testid="analysis-back-btn">Edit the description</Button>
        </div>
      )}
      {failed && <p className="flex items-center gap-2 text-xs text-ink-muted"><AlertTriangle size={14} /> Your draft is saved.</p>}
    </div>
  );
}
